import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useCompactViewport } from '~/hooks/useCompactViewport'
import { Typography } from '../common/Typography'
import { DateRangePanel } from './DateRangePanel'
import type { TimeRangeMode } from './DialogUsageDisplayContent'

// ============================================================================
// Icons
// ============================================================================

/** Caret down icon for dropdown trigger */
const CaretDownIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    className={className}
  >
    <path
      d='M6 9L12 15L18 9'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

/** Caret right icon for "Select" menu item */
const CaretRightIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    className={className}
  >
    <path
      d='M9 6L15 12L9 18'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

// ============================================================================
// Types
// ============================================================================

type DropdownView = 'menu' | 'datePicker'

interface BillingPeriodDropdownProps {
  /** Current time range mode */
  timeRangeMode: TimeRangeMode
  /** Callback when mode changes */
  onTimeRangeModeChange: (mode: TimeRangeMode) => void
  /** Billing period start date string */
  periodStart?: string
  /** Billing period end date string */
  periodEnd?: string
  /** Custom date range start */
  customStartDate?: string
  /** Custom date range end */
  customEndDate?: string
  /** Date formatter function */
  formatDate: (date?: string) => string
  /** Called when custom date range is selected via date picker */
  onCustomStartDateChange?: (date: string) => void
  /** Called when custom date range is selected via date picker */
  onCustomEndDateChange?: (date: string) => void
  /** YYYY-MM-DD bounds for the date picker (usage: epoch → today) */
  minSelectableDate?: string
  maxSelectableDate?: string
  /** Additional classes for trigger wrapper */
  className?: string
}

// ============================================================================
// Component
// ============================================================================

export const BillingPeriodDropdown: React.FC<BillingPeriodDropdownProps> = ({
  timeRangeMode,
  onTimeRangeModeChange,
  periodStart,
  periodEnd,
  customStartDate,
  customEndDate,
  formatDate,
  onCustomStartDateChange,
  onCustomEndDateChange,
  minSelectableDate,
  maxSelectableDate,
  className
}) => {
  const isCompactViewport = useCompactViewport()
  const [isOpen, setIsOpen] = useState(false)
  const [view, setView] = useState<DropdownView>('menu')
  const triggerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })

  // Trigger display
  const triggerLabel =
    timeRangeMode === 'billing_period' ? 'Current Billing Period' : 'Selected Billing Period'

  const billingPeriodValue = periodStart
    ? `${formatDate(periodStart)} – ${periodEnd ? formatDate(periodEnd) : 'Present'}`
    : '—'

  const customDateValue =
    customStartDate && customEndDate
      ? `${formatDate(customStartDate)} – ${formatDate(customEndDate)}`
      : 'Select'

  const triggerValue = timeRangeMode === 'billing_period' ? billingPeriodValue : customDateValue

  // Position dropdown below trigger
  const updatePosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      })
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      updatePosition()
      window.addEventListener('resize', updatePosition)
      window.addEventListener('scroll', updatePosition, true)
    }
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [isOpen, updatePosition])

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
        setView('menu')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Reset view when dropdown closes
  useEffect(() => {
    if (!isOpen) setView('menu')
  }, [isOpen])

  const handleToggle = () => setIsOpen(prev => !prev)

  const handleSelectBillingPeriod = () => {
    onTimeRangeModeChange('billing_period')
    setIsOpen(false)
  }

  const handleOpenDatePicker = () => {
    setView('datePicker')
  }

  const handleBackToMenu = () => {
    setView('menu')
  }

  const handleDateRangeSelected = (startDate: string, endDate: string) => {
    onTimeRangeModeChange('date_range')
    onCustomStartDateChange?.(startDate)
    onCustomEndDateChange?.(endDate)
    setIsOpen(false)
  }

  // ============================================================================
  // Render
  // ============================================================================

  const renderMenuView = () => (
    <>
      {/* Current Billing Period section */}
      <div
        onClick={handleSelectBillingPeriod}
        className='px-3 py-4 hover:bg-tokens-neutral-fill-fill-4 cursor-pointer transition-colors'
      >
        <div className='px-3'>
          <Typography
            fontFamily='geistMono'
            fontSize='labelSm'
            fontWeight='semibold'
            className='text-tokens-neutral-text-tertiary'
            style={{ fontSize: '11px', lineHeight: 'normal', letterSpacing: '-0.33px' }}
          >
            Current Billing Period
          </Typography>
        </div>
        <div className='h-8 px-3 flex items-center'>
          <Typography
            fontFamily='geist'
            fontSize='bodyMd'
            fontWeight='medium'
            className='text-tokens-neutral-text-primary truncate'
            style={{ fontSize: '14px', lineHeight: '22px' }}
          >
            {billingPeriodValue}
          </Typography>
        </div>
      </div>

      {/* Divider */}
      <div className='h-px bg-tokens-neutral-border-2' />

      {/* Selected Billing Period section */}
      <div
        onClick={handleOpenDatePicker}
        className='px-3 py-4 hover:bg-tokens-neutral-fill-fill-4 cursor-pointer transition-colors'
      >
        <div className='px-3'>
          <Typography
            fontFamily='geistMono'
            fontSize='labelSm'
            fontWeight='semibold'
            className='text-tokens-neutral-text-tertiary'
            style={{ fontSize: '11px', lineHeight: 'normal', letterSpacing: '-0.33px' }}
          >
            Selected Billing Period
          </Typography>
        </div>
        <div className='h-8 px-3 flex items-center justify-between'>
          <Typography
            fontFamily='geist'
            fontSize='bodyMd'
            fontWeight='medium'
            className='text-tokens-neutral-text-primary truncate'
            style={{ fontSize: '14px', lineHeight: '22px' }}
          >
            {timeRangeMode === 'date_range' ? customDateValue : 'Select'}
          </Typography>
          <CaretRightIcon className='size-6 text-tokens-neutral-text-primary flex-shrink-0' />
        </div>
      </div>
    </>
  )

  const renderDatePickerView = () => (
    <DateRangePanel
      initialStartDate={customStartDate}
      initialEndDate={customEndDate}
      minSelectableDate={minSelectableDate}
      maxSelectableDate={maxSelectableDate}
      onRangeSelect={handleDateRangeSelected}
      onBack={handleBackToMenu}
    />
  )

  return (
    <>
      {/* Trigger */}
      <div
        ref={triggerRef}
        onClick={handleToggle}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleToggle()
          } else if (e.key === 'Escape') {
            setIsOpen(false)
          }
        }}
        tabIndex={0}
        className={`min-h-[48px] w-full sm:min-w-[280px] sm:max-w-md px-4 py-3 border border-tokens-neutral-border-1 rounded-[12px] flex items-center justify-between cursor-pointer bg-transparent hover:bg-tokens-neutral-fill-fill-2 transition-colors select-none ${className ?? ''}`}
      >
        <div className='flex-1 flex flex-col gap-1 min-w-0' title={triggerValue}>
          <Typography
            fontFamily='geistMono'
            fontSize='labelSm'
            fontWeight='semibold'
            className='text-tokens-neutral-text-tertiary truncate'
            style={{ fontSize: '11px', lineHeight: 'normal', letterSpacing: '-0.33px' }}
          >
            {triggerLabel}
          </Typography>
          <Typography
            fontFamily='geist'
            fontSize='bodyMd'
            fontWeight='medium'
            className='text-tokens-neutral-text-primary line-clamp-2 break-words whitespace-normal'
            style={{ fontSize: '14px', lineHeight: '22px' }}
          >
            {triggerValue}
          </Typography>
        </div>
        <CaretDownIcon className='size-6 text-tokens-neutral-text-primary flex-shrink-0 ml-2 select-none' />
      </div>

      {/* Dropdown (portal) */}
      {isOpen &&
        typeof window !== 'undefined' &&
        createPortal(
          <div
            ref={dropdownRef}
            className='bg-white rounded-[16px] overflow-hidden min-w-0'
            style={{
              position: 'absolute',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              /**
               * Date picker content is ~560px (two 280px months) + horizontal padding. On desktop we
               * previously used width: undefined so the panel grew with content — invalid ranges
               * (hint copy + native date input intrinsic width) made the modal extremely wide.
               */
              width:
                view === 'datePicker'
                  ? Math.max(dropdownPosition.width, isCompactViewport ? 0 : 592)
                  : dropdownPosition.width,
              maxWidth: view === 'datePicker' ? 'calc(100vw - 24px)' : undefined,
              zIndex: 9999,
              boxShadow:
                '0px 6px 16px 0px rgba(0,0,0,0.08), 0px 3px 6px -4px rgba(0,0,0,0.12), 0px 9px 28px 8px rgba(0,0,0,0.05)'
            }}
          >
            {view === 'menu' ? renderMenuView() : renderDatePickerView()}
          </div>,
          document.body
        )}
    </>
  )
}

export default BillingPeriodDropdown
